import { useEffect, useMemo, useState } from "react";
import {
  loadStripe,
  StripeCheckoutPaymentElementOptions,
} from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
  ExpressCheckoutElement,
} from "@stripe/react-stripe-js";

import * as Api from "@modules/api";
import {
  createBehaviourEvent,
  createMyPaymentIntent,
  setAlert,
  useAppSelector,
  useCreateMyPaymentIntent,
} from "@lib/stores";
import { AlertType } from "@lib/stores/common/types";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { translate } from "@modules/localizations/translate";
import {
  getArabicStripeErrorMessage,
  StripeDeclineCode,
  StripeErrorCode,
  TrackedQueryStringBuilder,
} from "../Checkout";
import { SVG } from "@components/items/common/SVG";

interface CreditCheckoutProps {
  product: Api.Server.ProductGroupProductOutput;
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
}

let paymentIntentFetched = false;

export const CreditCheckout = ({
  product,
  onCancel,
  onSuccess,
}: CreditCheckoutProps) => {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      {product ? (
        <div className="mt-[13px]">
          <div className="mx-auto w-full">
            <StripeForm
              onCancel={onCancel}
              onSuccess={onSuccess}
              product={product}
              loadingStyle="border-black"
              price={
                <div className="flex items-center justify-center">
                  <div>{product.currencyCode || ""}</div>
                  <div className="ms-1">
                    {product.initialAmount ?? product.amount}
                  </div>
                  <div>{product.priceSuffix || ""}</div>
                </div>
              }
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface StripeFormProps {
  product: Api.Server.ProductGroupProductOutput;
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
  loadingStyle?: string;
  price?: string | JSX.Element;
}

export const StripeForm = ({
  product,
  onCancel,
  onSuccess,
  loadingStyle = "border-white",
  price,
}: StripeFormProps) => {
  const token = useAppSelector(({ auth }) => auth.tokens.token);
  const userId = useAppSelector(({ auth }) => auth.user.id);
  const email = useAppSelector(({ auth }) => auth.user.email);

  const {
    data: createMyPaymentIntentData,
    status: createMyPaymentIntentStatus,
  } = useCreateMyPaymentIntent();

  const mapProductName = () => {
    switch (product.subscriptionPeriod) {
      case "P1W":
        return translate("subscription.periods.P1W");
      case "P1M":
        return translate("subscription.periods.P1M");
      case "P1Y":
        return translate("subscription.periods.P1Y");
      case "":
        return translate("subscription.periods.LIFE_TIME");
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!token) {
      onCancel();
    }
  }, [token]);

  useEffect(() => {
    if (token && !paymentIntentFetched) {
      paymentIntentFetched = true;

      const trackedQueryStringBuilder = new TrackedQueryStringBuilder();

      trackedQueryStringBuilder
        .addQueryParamFromSessionStorage("ScCid", "_scCid")
        .addQueryParamFromSessionStorage("TwClid", "_twClid")
        .addQueryParamFromSessionStorage("ttclid", "_ttclid")
        .addQueryParamFromSessionStorage("vclid", "_vclid");

      const queryString = trackedQueryStringBuilder.build();

      console.log(queryString);

      createMyPaymentIntent(
        {
          lookupKey: product?.purchaseProviderKey,
          email,
          clickId: "",
          queryString,
          ignoreEphemeralKey: true,
        },
        undefined,
        (error: any) => {
          console.log(error);

          createBehaviourEvent({
            type: "PAY_ERROR",
            eventDate: new Date().toISOString(),
            userId,
            appVersion: "12.6.9",
            os: "web",
            country: "",
            deviceModel: "",
            osVersion: "",
            extraArguments: {
              error: error[0].message,
            },
          });

          setTimeout(() => {
            onCancel();
          }, 2200);
        }
      );
    }
  }, [
    token,
    userId,
    product.purchaseProviderKey,
    product.subscriptionPeriod,
    onCancel,
    email,
  ]);

  return (
    <div className="w-full">
      {createMyPaymentIntentStatus !== "loading" &&
      createMyPaymentIntentData ? (
        <div>
          <div className="mt-[14px] flex items-center justify-start md:flex-col md:justify-center">
            <TranslatedText
              text="full-books.add-balance"
              className="text-center text-[40px] font-bold text-black dark:text-white"
            />
          </div>
          <div className="mt-[37px] flex w-full items-start justify-center gap-x-[66px]">
            <div className="order-2 mt-[43px] w-full md:order-1 md:mt-0 md:w-[600px]">
              <Elements
                stripe={loadStripe(createMyPaymentIntentData.publishableKey)}
                options={{
                  clientSecret: createMyPaymentIntentData.clientSecret,
                }}
              >
                <Form
                  clientSecret={createMyPaymentIntentData.clientSecret}
                  onCancel={onCancel}
                  onSuccess={onSuccess}
                  product={product}
                  loadingStyle={loadingStyle}
                  price={price}
                />
              </Elements>
            </div>
            <div className="order-1 w-full max-w-[394px] md:order-2 md:w-[394px]">
              <TranslatedText
                text="full-books.balance-top-up"
                className="text-right text-[24px] font-medium leading-[36px] text-black dark:text-white"
              />
              <div className="mt-4 w-full overflow-hidden rounded-[18px] border border-primary md:mt-3 dark:border-[#64A6E1]">
                <div className="mt-3 flex w-full items-center justify-between px-4 text-black dark:text-white">
                  <div className="text-right text-[18px] font-medium">
                    {mapProductName()}
                  </div>
                  <div className="flex w-full items-center justify-between text-[18px] font-medium tracking-[-0.8px] md:text-[20px] md:leading-[30px]">
                    <div className="flex gap-2">
                      <SVG
                        src="/assets/icons/coin.svg"
                        className="my-auto h-6 w-6"
                      />
                      <div>{product.name}</div>
                    </div>
                    <div className="flex gap-2">
                      <div>{product.currencyCode}</div>
                      <div className="ms-1">{product.amount?.toFixed(2)}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between bg-[#38A7B424] px-4 py-4 dark:bg-[#89B8E2]">
                  <TranslatedText
                    text="subscription.titles.total price"
                    className="text-[18px] font-medium text-black md:text-[20px] md:leading-[30px] dark:text-white"
                  />
                  {product.initialAmount ? (
                    <div className="">
                      <div className="flex text-[18px] font-medium tracking-[-0.8px] text-black md:text-[20px] md:leading-[30px] dark:text-white">
                        <div>{product.currencyCode}</div>
                        <div className="ms-1">
                          {product.initialAmount?.toFixed(2)}
                        </div>
                      </div>
                      <div className="relative flex justify-end text-[15px] font-normal tracking-[-0.4px] text-black line-through decoration-[#FF0000] opacity-50 md:text-[15px] md:leading-[22px] dark:text-white">
                        <div>{product.currencyCode}</div>
                        <div className="">{product.amount?.toFixed(2)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex text-[18px] font-medium tracking-[-0.8px] text-black md:text-[20px] md:leading-[30px] dark:text-white">
                      <div>{product.currencyCode}</div>
                      <div className="ms-1">{product.amount?.toFixed(2)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : createMyPaymentIntentStatus === "loading" ? (
        <div className="flex flex-col items-center justify-center">
          <div className="mx-auto h-14 w-14">
            <div className="h-full w-full">
              <div
                className={`h-full w-full animate-spin rounded-full border-[3px] border-black border-b-transparent dark:border-white dark:border-b-transparent`}
              ></div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Form = ({
  clientSecret,
  onCancel,
  onSuccess,
  product,
  loadingStyle = "border-white",
  price,
}: {
  clientSecret: string;
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
  product: Api.Server.ProductGroupProductOutput;
  loadingStyle?: string;
  price?: string | JSX.Element;
}) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);

  const stripe = useStripe();

  const elements = useElements();

  const options = useOptions();

  const onSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    const { purchaseProviderKey, currencyCode, initialAmount, amount } =
      product;

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();

    if (submitError) {
      console.log(submitError);

      return;
    } else {
      setIsSubmitLoading(true);
    }

    const result = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url:
          window.location.origin +
          `/?provider=Stripe&key=${purchaseProviderKey}&currencyCode=${
            currencyCode && encodeURIComponent(currencyCode)
          }&price=${initialAmount || amount}`,
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    // const result = await stripe.confirmCardPayment(clientSecret, {
    //   payment_method: {
    //     card: elements.getElement(CardElement) as StripeCardElement,
    //   },
    // });

    setIsSubmitLoading(false);

    if (result.error) {
      const errorMsg = getArabicStripeErrorMessage(
        result.error?.code as StripeErrorCode | undefined,
        result.error?.decline_code as StripeDeclineCode | undefined
      );
      console.log(result);
      console.log(result.error.message);
      console.log(errorMsg);
      setAlert({
        for: "modal",
        show: true,
        messages: [
          {
            code: 400,
            field: null,
            message: errorMsg,
          },
        ],
        type: AlertType.ERROR,
      });
    }

    await onSuccess();
  };

  return (
    <div
      className={`relative ${
        isFormLoading ? "bg-transparent" : "border border-primary bg-[#F9FAFB]"
      } rounded-[19px] p-4`}
      dir="ltr"
    >
      {isFormLoading && (
        <div className="absolute left-0 top-0 z-20 h-full w-full">
          <div className="mx-auto mt-[19px] h-14 w-14 md:mt-10">
            <div className="h-full w-full">
              <div
                className={`h-full w-full animate-spin rounded-full border-[3px] border-black border-b-transparent dark:border-white dark:border-b-transparent`}
              ></div>
            </div>
          </div>
        </div>
      )}
      <ExpressCheckoutElement
        onConfirm={(event) => {
          onSubmit();
        }}
      />
      <PaymentElement
        options={options}
        onReady={() => {
          setIsFormLoading(false);
        }}
      />
      {/* <CardElement
          options={options}
          onReady={() => {
            setIsFormLoading(false);
          }}
        /> */}
      {!isFormLoading && (
        <>
          <div className="mx-auto mt-10 w-[358px]">
            <div className="h-[50px] w-full">
              <ShadcnButton
                className="!flex h-full w-full text-white"
                onClick={onSubmit}
                isLoading={isSubmitLoading}
                disabled={isSubmitLoading}
              >
                <TranslatedText
                  text={`full-books.${"buy-now"}`}
                  className="text-[20px] font-bold leading-[28px] text-white"
                />
              </ShadcnButton>
            </div>
          </div>
          {/* <div className="mt-4">
              <ButtonHeadless action={onCancel} bgStyle="bg-transparent">
                <div className="text-[#666c71]">
                  <p>رجوع</p>
                </div>
              </ButtonHeadless>
            </div> */}
        </>
      )}
    </div>
  );
};

const useOptions = () => {
  const options = useMemo<StripeCheckoutPaymentElementOptions>(
    () => ({
      layout: "accordion",
      paymentMethodOrder: ["apple_pay", "google_pay", "card"],
    }),
    []
  );

  return options;
};
